// Pure helpers that turn the broker statistics aggregation responses
// (`[{ _id: {...}, count }]`) into the shapes the dashboard renders. Kept
// separate from the view so the bucketing logic is easy to unit test.

// Application status is stored as either a legacy numeric code or a string key,
// so both are mapped to a single canonical bucket.
// Bucketing MUST mirror the applications-page status groups
// (STATUS_FILTER_VALUES in BrokerApplications.jsx) so a dashboard card's count
// always equals the list it deep-links to:
//   Incomplete = [0,2]  Submitted = [1,3,5,7]  Onboarded = [6,9]  Rejected = [4,8]
const STATUS_BUCKETS = {
  0: "draft", DRAFT: "draft",
  1: "submitted", SUBMITTED: "submitted",
  2: "needsInfo", NEEDS_CLARIFICATION: "needsInfo",
  3: "approved", APPROVED: "approved",
  4: "rejected", REJECTED: "rejected", 8: "rejected",
  // 5 = Account requested, 7 = Account creation failed: both post-approval
  // processing states that the applications page groups under "Submitted".
  // (7 was previously mis-bucketed as needsInfo, inflating Incomplete/Missing Info
  // and making those cards disagree with their deep-linked lists.)
  5: "inReview", IN_REVIEW: "inReview", 7: "inReview", ACCOUNT_CREATION_FAILED: "inReview",
  6: "provisioned", ACCOUNT_PROVISIONED: "provisioned",
  9: "expired", EXPIRED: "expired",
};

export const EMPTY_STATUS_STATS = {
  total: 0, draft: 0, submitted: 0, needsInfo: 0,
  approved: 0, rejected: 0, inReview: 0, provisioned: 0, expired: 0,
};

export const parseStatusStats = (rows = []) => {
  const buckets = { ...EMPTY_STATUS_STATS };
  (rows || []).forEach((item) => {
    const raw = item?._id?.status ?? item?.status;
    const key = STATUS_BUCKETS[raw] ?? STATUS_BUCKETS[String(raw)];
    const count = Number(item?.count || 0);
    buckets.total += count;
    if (key) buckets[key] += count;
  });
  return buckets;
};

// Exported so the dashboard builds its deep-link queries from the SAME sets it
// counts — guaranteeing each KYC card's number matches its filtered list.
export const KYC_PENDING = ["GENERATED", "SENT", "OPENED", "IN_PROGRESS", "KYC_PENDING"];
export const KYC_FAILED = ["FAILED", "KYC_FAILED", "REJECTED"];
const KYC_DONE = ["COMPLETED", "KYC_COMPLETE"];

// "KYC Due" = applications whose KYC Verification step is still Draft (0) or
// Incomplete (2). Fed by a statistics call that unwinds `steps` and groups by
// (steps.label, steps.status), e.g. { unwind: "steps", groupBy: { label:
// "steps.label", status: "steps.status" } }.
export const KYC_VERIFICATION_STEP_LABEL = "KYC Verification";
export const KYC_DUE_STEP_STATUSES = [0, 2];
export const parseKycDueFromSteps = (rows = []) => (rows || []).reduce((count, item) => {
  const label = item?._id?.label ?? item?.label;
  const status = Number(item?._id?.status ?? item?.status);
  return label === KYC_VERIFICATION_STEP_LABEL && KYC_DUE_STEP_STATUSES.includes(status)
    ? count + Number(item?.count || 0)
    : count;
}, 0);

export const EMPTY_KYC_STATS = { pending: 0, failed: 0, complete: 0 };

export const parseKycStats = (rows = []) => {
  const result = { ...EMPTY_KYC_STATS };
  (rows || []).forEach((item) => {
    const raw = String(item?._id?.kycStatus ?? item?.kycStatus ?? "").toUpperCase();
    const count = Number(item?.count || 0);
    if (KYC_PENDING.includes(raw)) result.pending += count;
    else if (KYC_FAILED.includes(raw)) result.failed += count;
    else if (KYC_DONE.includes(raw)) result.complete += count;
  });
  return result;
};

// Dynamic trend ranges. A fixed "last 6 months" window is meaningless for a
// brand-new broker, so the dashboard lets the user pick a window and buckets
// daily for short ranges, monthly for longer ones.
export const TREND_RANGES = [
  { key: "30d", label: "30D", unit: "day", count: 30 },
  { key: "3m", label: "3M", unit: "month", count: 3 },
  { key: "6m", label: "6M", unit: "month", count: 6 },
  { key: "12m", label: "12M", unit: "month", count: 12 },
];

export const getTrendRange = (key) => TREND_RANGES.find((item) => item.key === key) || TREND_RANGES[1];

// [from, to] unix-second window for the given range key.
export const rangeWindow = (key) => {
  const range = getTrendRange(key);
  const now = new Date();
  const start = range.unit === "day"
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - (range.count - 1))
    : new Date(now.getFullYear(), now.getMonth() - (range.count - 1), 1);
  return [Math.floor(start.getTime() / 1000), Math.floor(Date.now() / 1000)];
};

// Buckets the createdAt aggregation rows into the selected range's slots.
export const buildTrendSeries = (rows = [], key = "3m") => {
  const range = getTrendRange(key);
  const now = new Date();
  const labels = [];
  const slotKeys = [];

  if (range.unit === "day") {
    for (let i = range.count - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      labels.push(d.toLocaleDateString(undefined, { day: "numeric", month: "short" }));
      slotKeys.push(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }
  } else {
    for (let i = range.count - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push(d.toLocaleDateString(undefined, { month: "short", year: range.count > 6 ? "2-digit" : undefined }));
      slotKeys.push(`${d.getFullYear()}-${d.getMonth()}`);
    }
  }

  const counts = slotKeys.map(() => 0);
  (rows || []).forEach((item) => {
    const ts = Number(item?._id?.createdAt ?? item?.createdAt);
    if (!Number.isFinite(ts)) return;
    const d = new Date(ts * 1000);
    const slot = range.unit === "day"
      ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      : `${d.getFullYear()}-${d.getMonth()}`;
    const idx = slotKeys.indexOf(slot);
    if (idx >= 0) counts[idx] += Number(item?.count || 0);
  });

  return { labels, counts, total: counts.reduce((sum, value) => sum + value, 0) };
};
